import type { DerivedCampaignStatus } from "src/graphql";
import type { JourneyState } from "src/pages/journeys/types";
import { ChannelType } from "src/types/cep";

import { isDraftStatus, isEditableCampaignStatus } from "./constants";

/**
 * Where a save lands:
 * - `"publish"`: insert a content row and advance `published_version` to it —
 *   the dispatcher sends it immediately. Used on Draft / journey / standalone
 *   surfaces where there is no active send to stage against.
 * - `"stage"`: insert a content row and advance `draft_version` only, leaving
 *   `published_version` untouched until an explicit publish. Used on campaigns
 *   past Draft.
 */
export type ContentSaveMode = "publish" | "stage";

/**
 * Content editability + where a save lands. Discriminated union: `saveMode` is
 * present **only** when content is editable, so `{ canEdit: false, saveMode: … }`
 * cannot be constructed — the previously latent illegal pairing (a non-editable
 * surface computing a `saveMode`) is now unrepresentable.
 */
export type ContentEditability =
  | { canEdit: false }
  | { canEdit: true; saveMode: ContentSaveMode };

export type SurfaceEditability = {
  /** Structural edits: name rename, the editable overview layout, and (via
   *  `isDraft`) the Draft-only drawers. Campaign: the publishable set. Journey:
   *  draft/resetting/staged. Standalone: always. */
  canEditStructure: boolean;
  /** Narrower than `canEditStructure` — strict Draft-equivalent. Gates the
   *  draft-gated drawers (audience/schedule/metadata) and the mount-time
   *  auto-detach persist. Campaign: strict Draft. Journey:
   *  draft/resetting/staged. Standalone: always. */
  isDraft: boolean;
  content: ContentEditability;
};

export type SurfaceInput =
  | { trigger: "campaign"; derivedStatus: DerivedCampaignStatus | undefined }
  | { trigger: "journey"; journeyState: JourneyState }
  | { trigger: "standalone" };

/**
 * Journey states where structural edits (name rename, the full editable overview
 * layout, the Draft-only drawers) are allowed. `staged` folds in here because
 * journey staging is incomplete today — this mirrors the canvas, which already
 * treats `staged` as draft-like (`graph-context.tsx`).
 */
const STRUCTURAL_JOURNEY_STATES = new Set<JourneyState>([
  "draft",
  "resetting",
  "staged",
]);
export const isStructurallyEditableJourneyState = (s: JourneyState): boolean =>
  STRUCTURAL_JOURNEY_STATES.has(s);

/**
 * Journey states that are actively dispatching. Content edits on these must
 * **stage** against an explicit publish rather than going live immediately, so
 * an in-flight journey isn't force-published on every save. Every other state
 * (`draft`/`resetting`/`staged` — not yet sending — and `paused`/`off`/`deleting`
 * — not sending) publishes content immediately, matching the canvas behavior.
 */
const STAGING_JOURNEY_STATES = new Set<JourneyState>(["active", "draining"]);
export const getJourneyContentSaveMode = (s: JourneyState): ContentSaveMode =>
  STAGING_JOURNEY_STATES.has(s) ? "stage" : "publish";

/**
 * The single source of truth for message-editability policy. Pure function over
 * the surface's trigger + status; collapses the per-state matrix to one
 * verifiable place. Called by {@link useCurrentSurface} (for the outlet-mounted
 * surfaces) and by the message-detail loader (for the header, which renders
 * outside the outlet).
 */
export function deriveSurfaceEditability(
  input: SurfaceInput,
): SurfaceEditability {
  switch (input.trigger) {
    case "campaign": {
      const canEditStructure = isEditableCampaignStatus(input.derivedStatus);
      const isDraft = isDraftStatus(input.derivedStatus);
      return {
        canEditStructure,
        isDraft,
        content: canEditStructure
          ? { canEdit: true, saveMode: isDraft ? "publish" : "stage" }
          : { canEdit: false },
      };
    }
    case "journey": {
      const structural = isStructurallyEditableJourneyState(input.journeyState);
      return {
        canEditStructure: structural,
        isDraft: structural,
        content: {
          canEdit: true,
          saveMode: getJourneyContentSaveMode(input.journeyState),
        },
      };
    }
    case "standalone":
      return {
        canEditStructure: true,
        isDraft: true,
        content: { canEdit: true, saveMode: "publish" },
      };
  }
}

export type MetadataDrawerMode = "full" | "stage";

/**
 * Read by both the metadata route gate and the drawer so they can't disagree on
 * where a save lands. `null` means bounce:
 * - A surface that publishes content immediately but isn't Draft would strand a
 *   stage a draft nothing would publish.
 * - Staging carries only subject/preheader, which exist on email alone.
 */
export function getMetadataDrawerMode(
  surface: SurfaceEditability,
  channelType: ChannelType | null,
): MetadataDrawerMode | null {
  if (!surface.content.canEdit) return null;
  if (surface.isDraft) return "full";
  if (surface.content.saveMode !== "stage") return null;
  if (channelType !== ChannelType.Email) return null;
  return "stage";
}

/**
 * Maps the message outlet's raw trigger data to a {@link SurfaceInput}. Campaign
 * takes precedence (matches the loader's prior behavior); the two are mutually
 * exclusive in practice.
 */
export function messageSurfaceInput(args: {
  journeyState: JourneyState | null;
  campaignInfo: { derivedStatus: DerivedCampaignStatus | undefined } | null;
}): SurfaceInput {
  if (args.campaignInfo != null) {
    return {
      trigger: "campaign",
      derivedStatus: args.campaignInfo.derivedStatus,
    };
  }
  if (args.journeyState != null) {
    return { trigger: "journey", journeyState: args.journeyState };
  }
  return { trigger: "standalone" };
}
